import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../services/api';
import './AppointmentModal.css';

const AppointmentModal = ({ isOpen, onClose, onSave, appointment = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    customerId: '',
    customerName: '',
    customerPhone: '',
    date: '',
    time: '',
    duration: 60,
    type: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);

  const isEditMode = !!appointment;

  const appointmentTypes = [
    'Consultation',
    'Follow-up',
    'General Appointment',
    'Other'
  ];

  const durationOptions = [15, 30, 45, 60, 90, 120];

  useEffect(() => {
    if (appointment) {
      setFormData({
        title: appointment.title || '',
        customerId: appointment.customerId || '',
        customerName: appointment.customerName || '',
        customerPhone: appointment.customerPhone || '',
        date: appointment.date || '',
        time: appointment.time || '',
        duration: appointment.duration || 60,
        type: appointment.type || 'Consultation',
        notes: appointment.notes || ''
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        title: '',
        customerId: '',
        customerName: '',
        customerPhone: '',
        date: today,
        time: '',
        duration: 60,
        type: '',
        notes: ''
      });
    }
    setErrors({});
  }, [appointment, isOpen]);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setCustomerLoading(true);
        const response = await apiService.customers.getAll({ limit: 20, search: customerSearch || undefined });
        const items = Array.isArray(response?.data?.data) ? response.data.data : [];
        setCustomers(items);
      } catch (error) {
        console.error('Failed to load customers:', error);
      } finally {
        setCustomerLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadCustomers();
    }, 250);

    return () => clearTimeout(timer);
  }, [customerSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customerId: customer._id || customer.id || '',
      customerName: customer.name || '',
      customerPhone: customer.phoneNumber || customer.displayPhoneNumber || '',
    }));
    setCustomerSearch(customer.name || customer.phoneNumber || '');
  };

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    return customers.filter((customer) => {
      const search = customerSearch.toLowerCase();
      return [customer.name, customer.phoneNumber, customer.displayPhoneNumber, customer.email]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));
    });
  }, [customers, customerSearch]);

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Appointment title is required';
    }

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    }

    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = 'Phone number is required';
    }

    if (!formData.type) {
      newErrors.type = 'Appointment type is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today && !isEditMode) {
        newErrors.date = 'Date cannot be in the past';
      }
    }

    if (!formData.time) {
      newErrors.time = 'Time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onClose();
    }
  };

  if (!isOpen) return null;

  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content appointment-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Appointment' : 'Schedule New Appointment'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="title">Appointment Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Product Demo, Consultation Call"
              className={errors.title ? 'error' : ''}
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerSearch">Select Customer *</label>
              <input
                type="text"
                id="customerSearch"
                name="customerSearch"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  if (!e.target.value) {
                    setFormData(prev => ({ ...prev, customerName: '', customerPhone: '', customerId: '' }));
                  }
                }}
                placeholder="Search by customer name or phone"
                className={errors.customerName ? 'error' : ''}
              />
              {filteredCustomers.length > 0 && (
                <div className="customer-search-results">
                  {filteredCustomers.map((customer) => (
                    <button
                      key={customer._id || customer.id}
                      type="button"
                      className="customer-search-item"
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      <span>{customer.name || 'Unnamed customer'}</span>
                      <small>{customer.phoneNumber || customer.displayPhoneNumber || customer.email}</small>
                    </button>
                  ))}
                </div>
              )}
              {customerLoading && <span className="help-text">Loading customers...</span>}
              {errors.customerName && <span className="error-message">{errors.customerName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="type">Appointment Type *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={errors.type ? 'error form-select' : 'form-select'}
              >
                <option value="">Select appointment type</option>
                {appointmentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.type && <span className="error-message">{errors.type}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerPhone">Phone Number *</label>
              <input
                type="tel"
                id="customerPhone"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleChange}
                placeholder="+1 234 567 8900"
                className={errors.customerPhone ? 'error' : ''}
              />
              {errors.customerPhone && <span className="error-message">{errors.customerPhone}</span>}
            </div>

          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Date *</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                min={getMinDate()}
                className={errors.date ? 'error' : ''}
              />
              {errors.date && <span className="error-message">{errors.date}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="time">Time *</label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                className={errors.time ? 'error' : ''}
              />
              {errors.time && <span className="error-message">{errors.time}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="duration">Duration (minutes)</label>
              <select
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className="form-select"
              >
                {durationOptions.map(duration => (
                  <option key={duration} value={duration}>{duration} minutes</option>
                ))}
              </select>
            </div>

          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any notes about this appointment..."
              rows="4"
            />
            <span className="help-text">Internal notes about the appointment</span>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              {isEditMode ? 'Update Appointment' : 'Schedule Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;



